// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import { navigatorLock } from '@supabase/auth-js';
import * as Sentry from '@sentry/react';
import type { Database } from './types';

const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? "").trim();
/** Chave pública: publishable (sb_publishable_…) ou legado anon (JWT eyJ…). */
const SUPABASE_PUBLISHABLE_KEY = (
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  ""
).trim();

const missing = !SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY;
if (missing) {
  const msg =
    "Supabase não configurado: defina VITE_SUPABASE_URL e VITE_SUPABASE_PUBLISHABLE_KEY (ou VITE_SUPABASE_ANON_KEY) no .env na raiz. No Supabase: Settings → API → Project URL e chave anon/publishable. Reinicie npm run dev após alterar o .env.";
  if (import.meta.env.DEV) {
    console.error("[Supabase]", msg);
  }
  throw new Error(msg);
}

// Custom fetch: reporta erros Supabase/Postgres ao Sentry (ex: 42703 column does not exist)
// Erros Supabase retornam { data, error } — não lançam exceção, então unhandledrejection não dispara
const dsn = import.meta.env.VITE_SENTRY_DSN;
let supabaseClient: ReturnType<typeof createClient<Database>> | null = null;
const SESSION_INVALID_PATTERNS = [
  /session.*session_id.*does not exist|session_id claim in JWT does not exist/i,
  /token inválido|não autorizado/i,
];

function isSessionInvalid(
  res: Response,
  body: { message?: string; error?: string; code?: string },
  requestUrl: string,
): boolean {
  const code = typeof body?.code === 'string' ? body.code : '';
  if (
    code === 'refresh_token_not_found' ||
    code === 'invalid_grant' ||
    code === 'session_not_found'
  ) {
    return true;
  }
  const msg = [body?.message, body?.error].filter(Boolean).join(' ');
  if (/refresh.token.*not found|invalid.refresh.token/i.test(msg)) return true;
  if (SESSION_INVALID_PATTERNS.some((re) => re.test(msg))) return true;
  // Edge Functions retornam 401 com { error: "Unauthorized" } — a substring "auth" acionava
  // logout indevido. Só aplicar heurística ampla em APIs core (PostgREST, Auth, etc.).
  const url = requestUrl || (typeof res.url === 'string' ? res.url : '');
  if (/\/functions\/v1\//i.test(url)) return false;
  return (res.status === 401 || res.status === 403) && /token|session|auth|jwt/i.test(msg);
}

/**
 * Sentinel para evitar múltiplas chamadas concorrentes de hard sign-out
 * (vários requests podem disparar o handler ao mesmo tempo).
 */
let hardSignOutInFlight = false;

function isSupabaseAuthStorageKey(key: string): boolean {
  return (key.startsWith('sb-') && key.endsWith('-auth-token')) || key.startsWith('supabase.auth.');
}

/** Remove tokens de auth em sessionStorage (ativo) e localStorage (legado). */
function clearSupabaseAuthStorage(): void {
  if (typeof window === 'undefined') return;
  for (const storage of [window.sessionStorage, window.localStorage]) {
    if (!storage) continue;
    try {
      const keys: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const k = storage.key(i);
        if (k && isSupabaseAuthStorageKey(k)) keys.push(k);
      }
      keys.forEach((k) => {
        try { storage.removeItem(k); } catch { /* ignore */ }
      });
    } catch { /* ignore */ }
  }
}

/** Migra token legado do localStorage para sessionStorage (sessão por aba). */
function migrateAuthToSessionStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !isSupabaseAuthStorageKey(k)) continue;
      if (!sessionStorage.getItem(k)) {
        const v = localStorage.getItem(k);
        if (v) sessionStorage.setItem(k, v);
      }
      localStorage.removeItem(k);
    }
  } catch { /* ignore */ }
}

if (typeof window !== 'undefined') {
  migrateAuthToSessionStorage();
}

/**
 * Logout "à prova de deadlock":
 * - Limpa as chaves `sb-*-auth-token` diretamente (sem passar pelo GoTrueClient,
 *   que dependeria do Navigator Lock — o mesmo lock que pode estar travado).
 * - Faz um signOut "best-effort" com timeout curto, só pra ajudar em outras abas.
 * - Redireciona pra home, onde a tela de login será exibida.
 */
function hardSignOutAndRedirect(): void {
  if (hardSignOutInFlight) return;
  hardSignOutInFlight = true;
  clearSupabaseAuthStorage();

  void Promise.race([
    supabaseClient?.auth.signOut().catch(() => undefined) ?? Promise.resolve(),
    new Promise((r) => setTimeout(r, 500)),
  ]).finally(() => {
    if (typeof window !== 'undefined') {
      // Preserva a rota atual via `?redirect=` para o usuário voltar ao módulo após relogar.
      // Sem isso, qualquer estouro de Navigator Lock em consultas paralelas joga o usuário na Home sem contexto.
      const path = window.location.pathname || '/';
      const search = window.location.search || '';
      const target = path.startsWith('/dashboard') || path.startsWith('/acesso-externo')
        ? `${window.location.origin}/?redirect=${encodeURIComponent(path + search)}`
        : `${window.location.origin}/`;
      try {
        window.location.replace(target);
      } catch {
        window.location.href = target;
      }
    }
  });
}

const customFetch: typeof fetch = (input, init) => {
  const requestUrl = typeof input === 'string' ? input : (input && typeof (input as Request).url === 'string' ? (input as Request).url : '');
  return fetch(input, init).then(async (res) => {
    if (!res.ok) {
      try {
        const clone = res.clone();
        const body = await clone.json().catch(() => ({})) as { message?: string; error?: string; code?: string };
        const message = body?.message ?? body?.error ?? '';

        // Sessão JWT inválida / refresh revogado (401, 403 ou 400 em /token):
        // hard sign-out — não esperar `auth.signOut()` resolver, porque ele pega o
        // mesmo Navigator Lock que pode estar preso pelo refresh fracassado (deadlock).
        if (isSessionInvalid(res, body, requestUrl)) {
          hardSignOutAndRedirect();
        }

        if (dsn) {
          const code = (body as { code?: string })?.code;
          const skipSentry =
            message === 'Invalid login credentials' ||
            /invalid.*(login|credentials|password|email)/i.test(message) ||
            /invalid refresh token|refresh token not found/i.test(message) ||
            isSessionInvalid(res, body, requestUrl) ||
            /nenhuma chave de imagem|nenhum provedor de imagem/i.test(message) ||
            /Invalid key:/i.test(message) ||
            /new password should be different from the old password/i.test(message) ||
            /for security purposes, you can only request this after/i.test(message) ||
            /email not confirmed/i.test(message) ||
            /new row violates row-level security policy for table "activity_logs"/i.test(message) ||
            /column aasp_publicacoes\.advogados_citados does not exist/i.test(message) ||
            /adjustment_index.*financial_contracts.*schema cache/i.test(message) ||
            /already.*registered|e-mail.*já.*cadastrado/i.test(message) ||
            /já existe um convite pendente/i.test(message) ||
            res.status === 429;
          if ((code || message) && !skipSentry) {
            const err = new Error(message || `Supabase error ${res.status}`);
            (err as Error & { code?: string; details?: unknown }).code = code;
            (err as Error & { code?: string; details?: unknown }).details = (body as { details?: unknown })?.details;
            Sentry.captureException(err, {
              extra: { supabase: body, status: res.status, url: typeof input === 'string' ? input : input?.url },
            });
          }
        }
      } catch {
        // não quebrar o fluxo se o parse falhar
      }
    }
    return res;
  });
};

// Import the supabase client like this:
// import { supabase } from "@/shared/integrations/supabase/client";

/** Mínimo no Navigator Lock API: o supabase-js ainda não repassa `lockAcquireTimeout` ao GoTrueClient (fica 10s), o que estoura no portal com muitas leituras de sessão em paralelo. */
const AUTH_LOCK_MIN_MS = 90_000;

function authCrossTabLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> {
  if (typeof globalThis.navigator?.locks?.request !== 'function') {
    return fn();
  }
  const finalTimeout = acquireTimeout < 0
    ? acquireTimeout
    : Math.max(acquireTimeout, AUTH_LOCK_MIN_MS);
  return navigatorLock(name, finalTimeout, fn).catch((err: unknown) => {
    // Se o lock ficou preso por > 90s, é praticamente garantido que o refresh
    // anterior morreu sem soltar o lock (sessão revogada, aba congelada, etc).
    // Forçamos o hard sign-out pra não deixar a UI travada por minutos.
    const msg = err instanceof Error ? err.message : String(err);
    if (/Acquiring an exclusive Navigator LockManager lock|timed out/i.test(msg)) {
      hardSignOutAndRedirect();
    }
    throw err;
  });
}

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    // sessionStorage: mantém login enquanto a aba estiver aberta (mesmo inativa); ao fechar a aba, exige novo login
    storage: typeof window !== 'undefined' ? window.sessionStorage : undefined,
    persistSession: true,
    autoRefreshToken: true,
    lock: authCrossTabLock,
  },
  global: { fetch: customFetch },
});
supabaseClient = supabase;